import { useEffect, useState } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { ShoppingCart, ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAccount } from '../../context/AccountContext';
import {
  GET_EBAY_SETTINGS,
  GET_EBAY_POLICIES,
  BEGIN_EBAY_AUTH,
  UPDATE_EBAY_SETTINGS,
  DISCONNECT_EBAY,
} from '../../graphql';

/**
 * eBay connection.
 *
 * Nothing on this screen is a credential. Sellers grant access through eBay's
 * own consent flow, and the refresh token that results is held server-side in
 * the secret store — it never reaches the browser. What is editable here is
 * listing preference: marketplace, category, policies, how titles are built.
 */

const inputClassName =
  'w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

const MARKETPLACES = ['EBAY_US', 'EBAY_GB', 'EBAY_CA', 'EBAY_AU', 'EBAY_DE'];

const CONDITIONS = [
  { value: 'NEW', label: 'New' },
  { value: 'LIKE_NEW', label: 'Like new' },
  { value: 'USED_VERY_GOOD', label: 'Used — very good' },
  { value: 'USED_GOOD', label: 'Used — good' },
  { value: 'USED_ACCEPTABLE', label: 'Used — acceptable' },
];

const TOKENS = [
  '{year}',
  '{manufacturer}',
  '{set}',
  '{number}',
  '{player}',
  '{variant}',
  '{grade}',
];

export function EbayConnectionSettings() {
  const { accountId, isLoading: accountsLoading } = useAccount();
  const [form, setForm] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');

  const { data, loading, refetch } = useQuery(GET_EBAY_SETTINGS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const [loadPolicies, { data: policyData, loading: policiesLoading }] =
    useLazyQuery(GET_EBAY_POLICIES);
  const [beginAuth, { loading: starting }] = useMutation(BEGIN_EBAY_AUTH);
  const [updateSettings, { loading: saving }] = useMutation(UPDATE_EBAY_SETTINGS);
  const [disconnect] = useMutation(DISCONNECT_EBAY);

  const s = data?.ebaySettings;

  useEffect(() => {
    if (!s) return;
    setForm({
      marketplaceId: s.marketplaceId || 'EBAY_US',
      defaultCategoryId: s.defaultCategoryId || '',
      paymentPolicyId: s.paymentPolicyId || '',
      returnPolicyId: s.returnPolicyId || '',
      fulfillmentPolicyId: s.fulfillmentPolicyId || '',
      titlePattern: s.titlePattern || '',
      defaultCondition: s.defaultCondition || 'USED_VERY_GOOD',
      defaultQuantity: String(s.defaultQuantity ?? 1),
    });
  }, [s]);

  // The callback bounces back here with ?ebay=connected|denied|error.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('ebay');
    if (!result) return;
    if (result === 'connected') setMessage('eBay connected.');
    else if (result === 'denied') setMessage('You declined the eBay authorization.');
    else setMessage(params.get('message') || 'Could not connect to eBay.');
    void refetch();
  }, [refetch]);

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const handleConnect = async () => {
    if (!accountId) return;
    const { data: r } = await beginAuth({
      variables: { accountId, returnTo: window.location.pathname + window.location.search },
    });
    const url = r?.beginEbayAuth?.url;
    // Full navigation, not a popup: eBay's consent page refuses to be framed
    // and popup blockers eat the alternative.
    if (url) window.location.assign(url);
  };

  const handleDisconnect = async () => {
    if (!accountId) return;
    if (
      !window.confirm(
        'Disconnect eBay?\n\nDrafts already on eBay stay there. You will need to reconnect before pushing any more listings.'
      )
    ) {
      return;
    }
    await disconnect({ variables: { accountId } });
    setMessage('eBay disconnected.');
    await refetch();
  };

  const handleSave = async () => {
    if (!accountId) return;
    await updateSettings({
      variables: {
        accountId,
        input: {
          marketplaceId: form.marketplaceId || null,
          defaultCategoryId: form.defaultCategoryId || null,
          paymentPolicyId: form.paymentPolicyId || null,
          returnPolicyId: form.returnPolicyId || null,
          fulfillmentPolicyId: form.fulfillmentPolicyId || null,
          titlePattern: form.titlePattern || null,
          defaultCondition: form.defaultCondition || null,
          defaultQuantity: Number(form.defaultQuantity) || 1,
        },
      },
    });
    setMessage('Saved.');
    await refetch();
  };

  if (accountsLoading || loading) return <div className="p-6 text-cmp-ink-2">Loading…</div>;

  const policies = policyData?.ebayPolicies;

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold text-cmp-ink flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" /> eBay
        </h2>
        <p className="text-sm text-cmp-ink-2 mt-1">
          Connect your eBay seller account to push cards as draft listings. You authorize through
          eBay — we never see your eBay password or API keys.
        </p>
      </div>

      <Card className="p-4 space-y-3 bg-cmp-raise border border-cmp-line text-cmp-ink">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            {s?.connected ? (
              <>
                <Badge>connected</Badge>
                <span className="text-sm text-cmp-ink">{s.ebayUsername || 'eBay account'}</span>
              </>
            ) : (
              <Badge variant="outline">not connected</Badge>
            )}
            <Badge variant="outline" className="text-[10px]">
              {s?.environment || 'production'}
            </Badge>
          </div>
          {s?.connected ? (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => void handleConnect()}>
                Reconnect
              </Button>
              <Button variant="ghost" size="sm" onClick={() => void handleDisconnect()}>
                Disconnect
              </Button>
            </div>
          ) : (
            <Button onClick={() => void handleConnect()} disabled={starting} className="gap-2">
              <ExternalLink className="w-4 h-4" /> Connect eBay
            </Button>
          )}
        </div>

        {s?.environmentMismatch && (
          <p className="text-xs text-amber-400 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            This connection was made against eBay {s.environment}, but the server now uses a
            different environment. Reconnect before pushing listings.
          </p>
        )}

        {s?.expiringSoon && !s?.environmentMismatch && (
          <p className="text-xs text-amber-400 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            Your eBay authorization expires{' '}
            {s.refreshExpiresAt ? new Date(s.refreshExpiresAt).toLocaleDateString() : 'soon'}.
            Reconnect to avoid interruption.
          </p>
        )}

        {message && <p className="text-xs text-cmp-ink-2">{message}</p>}
      </Card>

      <Card className="p-4 space-y-4 bg-cmp-raise border border-cmp-line text-cmp-ink">
        <h3 className="text-sm font-medium text-cmp-ink">Listing defaults</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="marketplace">Marketplace</Label>
            <select
              id="marketplace"
              value={form.marketplaceId || 'EBAY_US'}
              onChange={(e) => set('marketplaceId', e.target.value)}
              className={inputClassName}
            >
              {MARKETPLACES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="category">Default category ID</Label>
            <Input
              id="category"
              value={form.defaultCategoryId || ''}
              onChange={(e) => set('defaultCategoryId', e.target.value)}
              placeholder="261328 (Baseball cards)"
              className={inputClassName}
            />
          </div>
          <div>
            <Label htmlFor="condition">Default condition</Label>
            <select
              id="condition"
              value={form.defaultCondition || 'USED_VERY_GOOD'}
              onChange={(e) => set('defaultCondition', e.target.value)}
              className={inputClassName}
            >
              {CONDITIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="qty">Default quantity</Label>
            <Input
              id="qty"
              type="number"
              min={0}
              value={form.defaultQuantity || '1'}
              onChange={(e) => set('defaultQuantity', e.target.value)}
              className={inputClassName}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="title">Title pattern</Label>
          <Input
            id="title"
            value={form.titlePattern || ''}
            onChange={(e) => set('titlePattern', e.target.value)}
            className={inputClassName}
          />
          <p className="text-[11px] text-cmp-ink-2 mt-1">Tokens: {TOKENS.join('  ')}</p>
        </div>

        {/* Policies are eBay's, so they can only be listed once connected. */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Business policies</Label>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={!s?.connected || policiesLoading}
              onClick={() => accountId && loadPolicies({ variables: { accountId } })}
            >
              <RefreshCw className="w-3 h-3" />
              {policiesLoading ? 'Loading…' : 'Load from eBay'}
            </Button>
          </div>
          {!s?.connected && (
            <p className="text-[11px] text-cmp-ink-2">
              Connect eBay to load your payment, return and shipping policies. eBay requires all
              three before an offer can be published.
            </p>
          )}
          {policies && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(
                [
                  ['paymentPolicyId', 'Payment', policies.payment],
                  ['returnPolicyId', 'Return', policies.return],
                  ['fulfillmentPolicyId', 'Shipping', policies.fulfillment],
                ] as const
              ).map(([key, label, list]) => (
                <div key={key}>
                  <Label htmlFor={key}>{label}</Label>
                  <select
                    id={key}
                    value={form[key] || ''}
                    onChange={(e) => set(key, e.target.value)}
                    className={inputClassName}
                  >
                    <option value="">— none —</option>
                    {(list || []).map((p: { id: string; name: string }) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Card>
    </div>
  );
}
